import { useState, useRef } from "react";
import { Modal, IconButton } from "rsuite";
import { format } from "date-fns";
import { DataTable, Button } from "../../../components";
import logger from "../../../utils/logger";
import { FaFileDownload } from "react-icons/fa";
import { useExportService } from "../ExportServiceContext";
import TrashIcon from "@rsuite/icons/Trash";
import ToastService from "../../../services/toast.service";
import "./exportsTable.css";

function ExportsTable({
  data,
  sortColumn,
  sortType,
  onSortColumn,
  loading,
  activeFilters,
  onFilterClick,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}) {
  const ExportService = useExportService();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const exportToDelete = useRef(null);
  const [deleting, setDeleting] = useState(false);

  async function handleDownloadClick(fileData, finance = false) {
    try {
      await ExportService.downloadExportDocument(fileData, finance);
    } catch (error) {

    }
  }

  function handleDeleteClick(exportId) {
    exportToDelete.current = exportId;
    setDeleteModalOpen(true);
  }

  function confirmDelete() {
    setDeleting(true);
    ExportService.deleteExport(exportToDelete.current)
      .then(() => {
        logger.info("Export deleted successfully");
        ToastService.success("Export deleted successfully");
      })
      .catch((error) => {
        logger.error("Error deleting export:", error);
        ToastService.error(`Failed to delete export: ${error.message || "Unknown error"}`);
      })
      .finally(() => {
        setDeleting(false);
        exportToDelete.current = null;
        setDeleteModalOpen(false);
      });
  }

  function cancelDelete() {
    setDeleteModalOpen(false);
    exportToDelete.current = null;
  }

  const columns = [
    {
      key: "exportFile",
      dataKey: "exportFile",
      title: "CMS Download",
      width: 100,
      sortable: false,
      filterable: false,
      render: (rowData) =>
        rowData.exportFile ? (
          <IconButton
            icon={<FaFileDownload />}
            size="xs"
            appearance="subtle"
            onClick={() => {
              handleDownloadClick(rowData);
            }}
            title="Download CMS File"
            className="download-button"
          />
        ) : (
          <span className="no-file">No File</span>
        ),
    },
    {
      key: "financeExportFile",
      dataKey: "financeExportFile",
      title: "Finance Download",
      width: 100,
      sortable: false,
      filterable: false,
      render: (rowData) =>
        rowData.financeExportFile ? (
          <IconButton
            icon={<FaFileDownload />}
            size="xs"
            appearance="subtle"
            onClick={() => {
              handleDownloadClick(rowData, true);
            }}
            title="Download Finance File"
            className="download-button"
          />
        ) : (
          <span className="no-file">No File</span>
        ),
    },
    {
      key: "postingDate",
      dataKey: "postingDate",
      title: "Posting Date",
      flexGrow: 1,
      sortable: true,
      filterable: true,
      dataType: "date",
      render: (rowData) => (rowData.postingDate ? format(new Date(rowData.postingDate), "dd-MM-yyyy") : "-"),
    },
    {
      key: "grandTotal",
      dataKey: "grandTotal",
      title: "Grand Total",
      width: 120,
      sortable: true,
      filterable: false,
      render: (rowData) => rowData.grandTotal ? `$${rowData.grandTotal?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-",
    },
    {
      key: "createdAt",
      dataKey: "createdAt",
      title: "Created At",
      width: 180,
      flexGrow: 1,
      sortable: true,
      filterable: false,
      render: (rowData) => (rowData.createdAt ? format(new Date(rowData.createdAt), "dd-MM-yyyy HH:mm") : "-"),
    },
    {
      key: "delete",
      dataKey: "delete",
      title: "Delete",
      width: 100,
      sortable: false,
      filterable: false,
      render: (rowData) => (
        <IconButton
          icon={<TrashIcon />}
          size="xs"
          appearance="subtle"
          onClick={() => handleDeleteClick(rowData?.id)}
          title="Delete Export"
          className="delete-button"
        />
      ),
    },
  ];

  return (
    <>
      <DataTable
        data-testid="exports-table"
        data={data}
        columns={columns}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSortColumn}
        loading={loading}
        activeFilters={activeFilters}
        onFilterClick={onFilterClick}
        expandable={false}
        showRowCount={true}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
        <Modal open={deleteModalOpen} onClose={cancelDelete} size="sm">
        <Modal.Header>
          <Modal.Title>Delete Export</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the export"?
          <br />
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={confirmDelete} data-testid="confirm-delete-export" color="red" text="Delete" />
          <Button onClick={cancelDelete} disabled={deleting} data-testid="cancel-delete-export" text="Cancel" color="blue" />
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ExportsTable;
